/**
 * Employee Trainings Tab Component
 * 
 * This component is extracted from EmployeeForm.jsx for modularity.
 * It maintains the exact same structure and data-testid attributes.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { DatePickerDE } from '../../components/ui/date-picker-de';
import { GraduationCap, Plus, Trash2, ExternalLink, X } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateDE } from './helpers';
import { openAuthFile } from '../../lib/openAuthFile';
import { FileDropzone } from '../../components/ui/file-dropzone';
import { ConfirmDeleteDialog } from '../../components/ui/confirm-delete-dialog';
import { ExpiryBadge } from '../profile/profileHelpers';

const TrainingsTab = ({
  formData,
  newTraining,
  setNewTraining,
  addTraining,
  removeTraining,
  handleCertificateUpload,
  uploading,
  newCertification,
  setNewCertification,
  addCertification,
  removeCertification,
}) => {
  return (
    <Card className="border-border/60">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <GraduationCap className="h-5 w-5 text-blue-600" />
          Weiterbildungen & Zertifikate
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Existing Trainings */}
        {formData.trainings?.length > 0 && (
          <div className="space-y-3">
            <Label>Bisherige Weiterbildungen</Label>
            {formData.trainings.map((training) => (
              <div
                key={training.id}
                className="flex items-start justify-between p-4 rounded-lg bg-secondary/30"
              >
                <div className="space-y-1">
                  <p className="font-medium">{training.title}</p>
                  <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                    {training.provider && <span>Anbieter: {training.provider}</span>}
                    <span>Abgeschlossen: {formatDateDE(training.completed_date || training.date)}</span>
                    {training.expiry_date && (
                      <ExpiryBadge expiry={training.expiry_date} />
                    )}
                    {training.hours && <span>{training.hours}h</span>}
                  </div>
                  {training.certificate_url && (
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={() =>
                          openAuthFile(training.certificate_url, { filename: training.certificate_filename })
                            .catch((err) => toast.error(`Anzeigen fehlgeschlagen: ${err.message || err}`))
                        }
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                        data-testid={`open-training-cert-${training.id}`}
                      >
                        <ExternalLink className="h-3 w-3" />
                        {training.certificate_filename || 'Zertifikat anzeigen'}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          openAuthFile(training.certificate_url, { filename: training.certificate_filename || 'zertifikat.pdf', forceDownload: true })
                            .catch((err) => toast.error(`Download fehlgeschlagen: ${err.message || err}`))
                        }
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary hover:underline"
                        data-testid={`download-training-cert-${training.id}`}
                      >
                        Herunterladen
                      </button>
                    </div>
                  )}
                </div>
                <ConfirmDeleteDialog
                  trigger={
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      data-testid={`remove-training-${training.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  }
                  title="Weiterbildung löschen?"
                  description={`Möchten Sie die Weiterbildung „${training.title}“ wirklich entfernen? Hochgeladene Zertifikate werden nicht mehr verlinkt sein.`}
                  onConfirm={() => removeTraining(training.id)}
                  testIdPrefix={`remove-training-${training.id}`}
                />
              </div>
            ))}
          </div>
        )}

        {/* Add New Training */}
        <div className="space-y-4 p-4 rounded-lg border border-dashed border-border">
          <Label className="text-base font-medium">Neue Weiterbildung hinzufügen</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="training_title">Titel *</Label>
              <Input
                id="training_title"
                value={newTraining.title || ''}
                onChange={(e) => setNewTraining({ ...newTraining, title: e.target.value })}
                placeholder="z.B. Erste-Hilfe-Kurs"
                data-testid="training-title"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="training_provider">Anbieter</Label>
              <Input
                id="training_provider"
                value={newTraining.provider || ''}
                onChange={(e) => setNewTraining({ ...newTraining, provider: e.target.value })}
                placeholder="z.B. DRK"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="training_date">Abgeschlossen am *</Label>
              <DatePickerDE
                value={newTraining.date || ''}
                onChange={(v) => setNewTraining({ ...newTraining, date: v })}
                placeholder="TT.MM.JJJJ"
                testId="training-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="training_expiry">Gültig bis (optional)</Label>
              <DatePickerDE
                value={newTraining.expiry_date || ''}
                onChange={(v) => setNewTraining({ ...newTraining, expiry_date: v })}
                placeholder="Ablaufdatum"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="training_hours">Stunden</Label>
              <Input
                id="training_hours"
                type="number"
                value={newTraining.hours || ''}
                onChange={(e) => setNewTraining({ ...newTraining, hours: e.target.value })}
                placeholder="z.B. 8"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Zertifikat hochladen</Label>
              <FileDropzone
                accept=".pdf,.jpg,.jpeg,.png"
                onUpload={(e) => handleCertificateUpload(e, true)}
                uploading={uploading}
                filename={newTraining.certificate_filename}
                testId="training-certificate-dropzone"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="training_description">Beschreibung</Label>
            <Textarea
              id="training_description"
              value={newTraining.description || ''}
              onChange={(e) => setNewTraining({ ...newTraining, description: e.target.value })}
              placeholder="Optionale Beschreibung..."
              rows={2}
            />
          </div>
          <Button
            type="button"
            onClick={addTraining}
            variant="outline"
            className="w-full"
            data-testid="add-training-btn"
          >
            <Plus className="mr-2 h-4 w-4" />
            Weiterbildung hinzufügen
          </Button>
        </div>

        {/* Zusätzliche Kenntnisse & Fähigkeiten (Tags, ohne Nachweis) */}
        <div className="space-y-4 pt-2 border-t border-border/40">
          <Label>Zusätzliche Kenntnisse & Fähigkeiten</Label>
          <p className="text-xs text-muted-foreground">
            Für Kenntnisse ohne Nachweis-Dokument (z.B. Sprachkenntnisse, Software-Skills)
          </p>
          <div className="flex gap-2">
            <Input
              placeholder="z.B. MS Office, Englisch C1..."
              value={newCertification}
              onChange={(e) => setNewCertification(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCertification())}
              data-testid="new-certification-input"
            />
            <Button type="button" onClick={addCertification} variant="outline" data-testid="add-certification-btn">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(formData.certifications || []).map((cert, index) => (
              <div
                key={`cert-${cert}-${index}`}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent/10 text-accent text-sm"
              >
                {cert}
                <ConfirmDeleteDialog
                  trigger={
                    <button
                      type="button"
                      className="hover:text-destructive"
                      data-testid={`remove-certification-${index}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  }
                  title="Kenntnis entfernen?"
                  description={`Möchten Sie „${cert}“ wirklich aus den Kenntnissen entfernen?`}
                  onConfirm={() => removeCertification(index)}
                  testIdPrefix={`remove-certification-${index}`}
                />
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TrainingsTab;
